import React from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import Recruiter from "../../apiResources/Recruiter";
import Employer from "../../apiResources/Employer";
import RecruiterUser from "../../apiResources/RecruiterUser";
import EmployerUser from "../../apiResources/EmployerUser";
import TextLink from "../library/TextLink";
import { TextInputField } from "../library/Form";
import SubmitButton from "../library/Form/SubmitButton";
import SettingLabel from "./SettingLabel";

const AddressSettingsOption = (props: Props) => {
  const {
    user,
    company,
    isEditing = false,
    disabled = false,
    onEditStart,
    onEditComplete,
  } = props;

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    try {
      company.assignAttributes(values);
      const success = await company.save();
      if (success) {
        onEditComplete();
      } else {
        formik.setErrors({
          addressLine1: company.errors.addressLine1?.fullMessage,
          addressLine2: company.errors.addressLine2?.fullMessage,
          townOrCity: company.errors.townOrCity?.fullMessage,
          postcode: company.errors.postcode?.fullMessage,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
      formik.setErrors({
        addressLine1: "Uh oh, something went wrong there",
      });
    } finally {
      company.rollback();
    }
  };

  const edit = () => {
    if (!disabled) {
      onEditStart();
    }
  };

  const noAddressDetails =
    !company.addressLine1 &&
    !company.addressLine2 &&
    !company.townOrCity &&
    !company.postcode;

  const mapLocation = company.postcode || company.townOrCity;

  if (!isEditing) {
    return (
      <section>
        <Header>
          <SettingLabel>Address</SettingLabel>
          {user.isAdmin && (
            <TextLink disabled={disabled} onClick={edit}>
              Edit
            </TextLink>
          )}
        </Header>
        {company.addressLine1 && <div>{company.addressLine1}</div>}
        {company.addressLine2 && <div>{company.addressLine2}</div>}
        {company.townOrCity && <div>{company.townOrCity}</div>}
        {company.postcode && <div>{company.postcode}</div>}

        {mapLocation && (
          <MapContainer>
            <iframe
              title="Map"
              src={`https://maps.google.com/maps?q=${mapLocation}&Roadmap&z=15&ie=UTF8&iwloc=&output=embed`}
              frameBorder="0"
              width="100%"
              height="100%"
              allowFullScreen
            />
          </MapContainer>
        )}

        {noAddressDetails && <div>Not specified</div>}
      </section>
    );
  }

  const initialValues: FormValues = {
    addressLine1: company.addressLine1,
    addressLine2: company.addressLine2,
    townOrCity: company.townOrCity,
    postcode: company.postcode,
  };

  const validation = Yup.object({
    addressLine1: Yup.string(),
    addressLine2: Yup.string(),
    townOrCity: Yup.string(),
    postcode: Yup.string(),
  });

  return (
    <section>
      <Formik
        initialValues={initialValues}
        onSubmit={save}
        validationSchema={validation}
      >
        <Form autoComplete="off">
          <Header>
            <SettingLabel>Address</SettingLabel>
            <TextLink onClick={onEditComplete}>Cancel</TextLink>
          </Header>
          <TextInputField label="Address Line 1" name="addressLine1" />
          <TextInputField label="Address Line 2" name="addressLine2" />
          <TextInputField label="Town or City" name="townOrCity" />
          <TextInputField label="Postcode" name="postcode" />
          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type FormValues = {
  addressLine1?: string;
  addressLine2?: string;
  townOrCity?: string;
  postcode?: string;
};

type Props = {
  user: RecruiterUser | EmployerUser;
  company: Recruiter | Employer;
  isEditing?: boolean;
  editable?: boolean;
  disabled?: boolean;
  onEditStart: () => void;
  onEditComplete: () => void;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const MapContainer = styled.div`
  margin-top: 1rem;
  width: 500px;
  max-width: 100%;
  height: 250px;
  background-color: ${({ theme }) => theme.colors.gray200};
`;

export default AddressSettingsOption;
